import { useState } from "react";
import { ApiError, fetchSources, startRun } from "../api/client";
import { Alert } from "../components/Alert";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { RunProgressBar } from "../components/RunProgressBar";
import { RunStateBadge } from "../components/RunStateBadge";
import { ScheduleCard } from "../components/ScheduleCard";
import { Spinner } from "../components/Spinner";
import { useAsyncData } from "../hooks/useAsyncData";
import type { UseRunStatusResult } from "../hooks/useRunStatus";
import { formatError } from "../utils/formatError";
import type { RunMode, RunState, StartRunRequest } from "../api/types";

const MODE_OPTIONS: { value: RunMode; label: string; description: string }[] = [
  {
    value: "incremental",
    label: "Incremental",
    description: "Index files that changed since the last run.",
  },
  { value: "full", label: "Full", description: "Re-index every file from scratch." },
  {
    value: "summarize",
    label: "Summarize",
    description: "Regenerate file summaries only, without re-indexing.",
  },
];

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "—";
}

/**
 * The tone for a run's `notice`. `interrupted` gets its own (warning, not error) tone because
 * nothing actually reported a failure — the process just stopped reporting. `succeeded` can still
 * carry a notice (e.g. "no run has ever been recorded"), which is informational, not a success
 * message in its own right.
 */
function noticeVariant(state: RunState): "error" | "warning" | "info" {
  if (state === "failed") {
    return "error";
  }
  if (state === "interrupted") {
    return "warning";
  }
  return "info";
}

interface RunsScreenProps {
  runStatus: UseRunStatusResult;
}

/** Registered sources and their configuration state, plus starting and monitoring indexing runs. */
export function RunsScreen({ runStatus }: RunsScreenProps): JSX.Element {
  const {
    data: sources,
    loading: sourcesLoading,
    error: sourcesError,
    reload: reloadSources,
  } = useAsyncData(() => fetchSources(), []);
  const { report, loading: statusLoading, error: statusError, reload: reloadStatus } = runStatus;

  const [mode, setMode] = useState<RunMode>("incremental");
  const [source, setSource] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const isRunning = report?.status.state === "running";

  const handleStart = async (): Promise<void> => {
    setStarting(true);
    setStartError(null);
    const body: StartRunRequest = { mode, source: source || undefined };
    try {
      await startRun(body);
      setConfirming(false);
      reloadStatus();
    } catch (error) {
      // A 409 here means a run started elsewhere in the gap between opening this dialog and
      // confirming it; that is an expected race, not a crash, so it gets the same inline
      // treatment as the last-admin-removal 409 does on the Admins screen.
      if (error instanceof ApiError && error.isConflict) {
        setStartError(error.detail);
      } else {
        setStartError(formatError(error));
      }
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="runs-screen">
      <section>
        <h2>Sources</h2>
        {sourcesLoading && <Spinner label="Loading sources…" />}
        {sourcesError && (
          <Alert variant="error">Could not load sources: {formatError(sourcesError)}</Alert>
        )}
        {!sourcesLoading && !sourcesError && (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Default</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {(sources ?? []).map((entry) => (
                <tr key={entry.name}>
                  <td>{entry.name}</td>
                  <td>{entry.is_default ? "Yes" : "—"}</td>
                  <td>
                    {entry.configured ? (
                      <span className="badge badge--ready">Configured</span>
                    ) : (
                      <span className="badge badge--attention">
                        Not configured — cannot run until credentials are set
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(sources ?? []).length === 0 && (
                <tr>
                  <td colSpan={3}>No sources are registered.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        <button
          type="button"
          className="button button--secondary button--small"
          onClick={reloadSources}
        >
          Refresh sources
        </button>
      </section>

      <section>
        <h2>Current run</h2>
        {statusLoading && <Spinner label="Loading run status…" />}
        {statusError && (
          <Alert variant="error">Could not load run status: {formatError(statusError)}</Alert>
        )}
        {report && (
          <div className="run-status-card">
            <RunStateBadge state={report.status.state} />
            <dl>
              <dt>Mode</dt>
              <dd>{report.status.mode ?? "—"}</dd>
              <dt>Source</dt>
              <dd>{report.status.source || "default"}</dd>
              <dt>Started</dt>
              <dd>{formatDate(report.status.started_at)}</dd>
              <dt>Finished</dt>
              <dd>{formatDate(report.status.finished_at)}</dd>
              <dt>Started by</dt>
              <dd>{report.status.started_by || "—"}</dd>
              <dt>Exit code</dt>
              <dd>{report.status.exit_code ?? "—"}</dd>
            </dl>
            {report.status.progress && (
              <RunProgressBar
                progress={report.status.progress}
                startedAt={report.status.started_at}
              />
            )}
            {report.status.notice && (
              <Alert variant={noticeVariant(report.status.state)}>{report.status.notice}</Alert>
            )}
            {report.log_tail.length > 0 && (
              <pre className="log-tail">{report.log_tail.join("\n")}</pre>
            )}
          </div>
        )}
      </section>

      <section>
        <h2>Start a run</h2>
        <p>
          Starting a run spends money and rewrites the index. It is refused, not queued, while
          another run is already in progress.
        </p>
        <div className="run-form">
          <label htmlFor="run-mode">Mode</label>
          <select
            id="run-mode"
            value={mode}
            onChange={(event) => setMode(event.target.value as RunMode)}
          >
            {MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className="run-form__description">
            {MODE_OPTIONS.find((option) => option.value === mode)?.description}
          </p>

          <label htmlFor="run-source">Source</label>
          <select
            id="run-source"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            <option value="">Default source</option>
            {(sources ?? []).map((entry) => (
              <option key={entry.name} value={entry.name} disabled={!entry.configured}>
                {entry.name}
                {entry.configured ? "" : " (not configured)"}
              </option>
            ))}
          </select>

          {startError && <Alert variant="error">{startError}</Alert>}

          <button
            type="button"
            className="button button--primary"
            onClick={() => setConfirming(true)}
            disabled={isRunning}
          >
            {isRunning ? "A run is already in progress" : "Start run…"}
          </button>
        </div>
      </section>

      <section>
        <h2>Schedule</h2>
        <p>
          When the indexer runs by itself. A scheduled run obeys the same one-at-a-time rule as a
          manual one: a slot that falls during a run is skipped, not queued.
        </p>
        <ScheduleCard sources={sources ?? []} />
      </section>

      {confirming && (
        <ConfirmDialog
          title="Start indexing run"
          message={
            `Start a "${mode}" run on ${source || "the default source"}? ` +
            "This will spend money and rewrite the index."
          }
          confirmLabel="Start run"
          busy={starting}
          onConfirm={handleStart}
          onCancel={() => {
            setConfirming(false);
            setStartError(null);
          }}
        />
      )}
    </div>
  );
}
